import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { copyText } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Shield, Copy, ExternalLink, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * Phase 5 — SPF / DKIM / DMARC Hinweis-Box (READ-ONLY).
 *
 * KEINE automatische DNS-Verifikation. Nur statische Hinweise + Copy-Buttons
 * mit den empfohlenen Records, damit Plattform-Admin/Mandant die DNS bei IONOS
 * einstellen kann.
 */
const DOCS_URL = "https://www.ionos.de/hilfe/e-mail/spf-dkim-dmarc/";

export default function EmailDnsHintCard({ variant = "tenant" }) {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/email/status");
        if (!cancelled) setStatus(r.data);
      } catch {
        if (!cancelled) setStatus({ enabled: false });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const senderDomain = (() => {
    const addr = status?.from_address || "";
    const at = addr.indexOf("@");
    return at >= 0 ? addr.slice(at + 1) : "";
  })();

  const records = [
    {
      type: "SPF",
      name: senderDomain || "ihre-domain.de",
      value: "v=spf1 include:_spf.perfora.net include:_spf-eu.ionos.com -all",
      hint: "Verifiziert IONOS als legitimen Absender. Wird oft schon von IONOS automatisch gesetzt.",
    },
    {
      type: "DKIM",
      name: `s1._domainkey.${senderDomain || "ihre-domain.de"}`,
      value: 'Bei IONOS unter "E-Mail → Domain & Postfach → DKIM aktivieren" generieren',
      hint: "DKIM-Schlüssel im IONOS-Webhost-Center aktivieren — IONOS legt den TXT-Record automatisch an.",
      isAction: true,
    },
    {
      type: "DMARC",
      name: `_dmarc.${senderDomain || "ihre-domain.de"}`,
      value: "v=DMARC1; p=quarantine; rua=mailto:postmaster@" + (senderDomain || "ihre-domain.de"),
      hint: "Definiert, was bei Spoof-Versuchen passieren soll. Start mit p=none (Monitor), später p=quarantine.",
    },
  ];

  const copy = async (text) => {
    const ok = await copyText(text);
    if (ok) toast.success("Kopiert");
    else toast.error("Kopieren fehlgeschlagen — bitte manuell markieren");
  };
  const accentClass = variant === "agency" ? "border-violet-200" : "border-orange-200";
  const iconColor = variant === "agency" ? "text-violet-600" : "text-orange-600";

  if (!status) return null;

  return (
    <Card className={`p-6 space-y-4 ${accentClass}`} data-testid="email-dns-hint-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2">
          <Shield className={`w-4 h-4 ${iconColor} mt-0.5`} />
          <div>
            <h2 className="text-lg font-semibold">E-Mail-Authentifizierung (SPF / DKIM / DMARC)</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Diese DNS-Einträge verbessern die Zustellbarkeit Ihrer Mails deutlich. <b>Pflicht für Versand &gt; 50 Mails/Tag</b>.
            </p>
          </div>
        </div>
        <Badge className="bg-slate-100 text-slate-700">
          <AlertCircle className="w-3 h-3 mr-1" /> Manuell bei DNS-Provider setzen
        </Badge>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-800" data-testid="email-dns-info-banner">
        <b>Hinweis:</b> revenioo.ai versendet derzeit über <code className="font-mono">{status?.from_address || "—"}</code>.
        Die DNS-Einträge müssen für die Domain <b>{senderDomain || "—"}</b> bei IONOS gesetzt werden — das ist Aufgabe des Plattform-Admins, nicht der Mandanten.
      </div>

      <div className="space-y-3" data-testid="email-dns-records">
        {records.map((r) => (
          <div key={r.type} className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`email-dns-record-${r.type.toLowerCase()}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Badge className="bg-slate-900 text-white text-[10px]">{r.type}</Badge>
                <code className="font-mono text-xs text-slate-700">{r.name}</code>
              </div>
              {!r.isAction && (
                <Button
                  data-testid={`email-dns-copy-${r.type.toLowerCase()}`}
                  size="sm" variant="ghost" className="h-7 px-2"
                  onClick={() => copy(r.value)}
                >
                  <Copy className="w-3 h-3 mr-1" /> Wert kopieren
                </Button>
              )}
            </div>
            <pre className="bg-slate-50 border border-slate-100 rounded px-3 py-2 text-[11px] overflow-x-auto whitespace-pre-wrap break-all">{r.value}</pre>
            <p className="text-[11px] text-slate-500">{r.hint}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-2 pt-1">
        <p className="text-[11px] text-slate-400">Keine automatische DNS-Verifikation — bitte selbst bei IONOS &amp; DNS-Provider prüfen.</p>
        <a
          href={DOCS_URL}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-slate-900"
          data-testid="email-dns-docs-link"
        >
          IONOS-Anleitung <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </Card>
  );
}

export const DnsCheckmark = CheckCircle2; // re-export for potential reuse
